<template>
    <div class="position-relative d-inline-block mr-1" @click.stop.prevent>
        <button class="btn btn-light border" @click.stop.prevent="expanded = !expanded">
            {{ 'Presets' | trans }}
            <i class="font-md" :class="{ 'fa fa-chevron-down': !expanded, 'fa fa-chevron-up': expanded }"></i>
        </button>

        <div v-if="expanded" class="dropdown-menu dropdown-menu-right br-8 show vue-preset-dropdown">
            <template v-if="isStatusDone">
                <PresetItem
                    v-for="preset in presets"
                    v-if="preset.target === target"
                    v-bind:key="preset.id_preset"
                    :preset="preset"
                    @delete-preset="deletePreset"
                    @update-preset="initPatchPreset"
                    @select-preset="setOptionsFromPreset"
                    @change-preset-name="changePresetName"
                    ref="PresetItem"
                ></PresetItem>

                <div v-if="Array.isArray(presets) && presets.length > 0" class="dropdown-divider"></div>

                <form v-if="!patchPresetInit">
                    <div class="form-group">
                        <label for="preset-input">{{ 'Save preset as' | trans }}</label>
                        <input
                            class="form-control"
                            :placeholder="'Enter preset name' | trans"
                            id="preset-input"
                            v-model="presetName"
                        />
                    </div>

                    <button class="btn btn-success m-b-10 m-l-20" @click.stop.prevent="savePreset">
                        {{ 'Save new preset' | trans }}
                    </button>
                </form>

                <button
                    v-if="patchPresetInit"
                    class="btn btn-success mb-2 ml-2 mt-2"
                    @click.stop.prevent="patchPreset(presetToUpdate)"
                >
                    {{ 'Update preset' | trans }}
                </button>
            </template>
            <LoadingAnimation
                v-else-if="isStatusLoading"
                animation-style="bars"
                classes="m-b-10 m-t-10"
            ></LoadingAnimation>
        </div>
    </div>
</template>

<script>
import { testIsFalseish, testIsNonEmptyString } from '../../../../../lib/test-and-assert/test-base';
import LoadingAnimation from '../LoadingAnimation';
import { PresetApi } from '../../../../../lib-sat/api/preset-api';
import { globalLogger } from '../../../../helper/global-logger';
import { pageNotification } from '../../../../helper/notify';
import DataStatus from '../mixins/DataStatus';
import TransFilter from '../mixins/TransFilter';
import PresetItem from './PresetItem';

const presetApi = new PresetApi();

export default {
    name: 'PresetSelector',
    components: { PresetItem, LoadingAnimation },
    mixins: [DataStatus, TransFilter],
    props: {
        initialPresets: Array,
    },
    mounted() {
        this.presets = this.initialPresets;
        document.addEventListener('click', this.close);

        if (this.targetSelector !== null) {
            this.targetSelector.addEventListener('change', this.updateTarget);
        }

        this.target = $('[name=target]').val();
        this.setStatusDone();
    },
    beforeDestroy() {
        document.removeEventListener('click', this.close);

        if (this.targetSelector !== null) {
            this.targetSelector.removeEventListener('change', this.updateTarget);
        }
    },
    filters: {
        truncate(str) {
            if (str.length < 50) {
                return str;
            }

            return `${str.substr(0, 50)}...`;
        },
    },
    methods: {
        close() {
            this.expanded = false;
        },
        updateTarget() {
            this.target = $('#inputTarget').val();
        },
        savePreset() {
            if (!testIsNonEmptyString(this.presetName)) {
                return;
            }

            const conversion = global.qgFeaturePage.getOptionsFromForm('#sendform');

            if (!conversion) {
                return;
            }

            const newPreset = {
                category: conversion.category,
                target: conversion.target,
                name: this.presetName,
                options: conversion.options,
            };

            this.setStatusLoading();
            presetApi
                .postPreset(newPreset)
                .then((preset) => {
                    this.presets.push(preset);
                    this.presetSaved = true;
                    this.presetName = '';
                    pageNotification.success('Preset saved');

                    setTimeout(() => {
                        this.presetSaved = false;
                    }, 1000);
                })
                .catch((e) => {
                    globalLogger.log('preset selector error - post preset', { error: e, presetData: newPreset });

                    pageNotification.error('Something went wrong, please try again later!');
                })
                .finally(() => {
                    this.setStatusDone();
                });
        },
        setOptionsFromPreset(preset) {
            for (const option in preset.options) {
                if (option === 'allow_multiple_outputs') {
                    continue;
                }

                const $element = $('[name="' + option + '"]');
                let value = preset.options[option];
                const elementType = $element.prop('nodeName');

                if (elementType === 'SELECT') {
                    let $optgroup = $element.find('optgroup[data-options-target="' + this.target + '"]');

                    //if the optgroup for the specific target does not exist,
                    //use the default one
                    if ($optgroup.length === 0) {
                        $optgroup = $element.find('optgroup[data-options-target="default"]');
                    }

                    //deselect default selected value
                    $optgroup.find(':selected').attr('selected', false);
                    let $option = $optgroup.find('option[value="' + value + '"]');

                    //if default optgroup also does not exist
                    //then the select does not have optgroups, so we select the
                    //option directly
                    if ($optgroup.length === 0) {
                        $option = $element.find('option[value="' + value + '"]');
                    }

                    if (option === 'resize_handling') {
                      $element.val(value).change();
                    }

                    //only using the attr function does not trigger the change event that is needed for some options
                    $option.attr('selected', 'selected').change();

                    const parentRadioId = $element.data('parent-radio-id');
                    if (typeof parentRadioId !== 'undefined') {
                        $('#' + parentRadioId).click();
                    }
                } else if (elementType === 'INPUT') {
                    $.each($element, (key, idx) => {
                        const element = $(idx);
                        if (element.data('value-type') === 'boolean') {
                            //For some reason jquery does not accept false as string
                            if (testIsFalseish(value)) {
                                value = false;
                            }

                            element.prop('checked', value);
                        } else if (
                            element.data('value-type') === 'integer' ||
                            element.data('value-type') === 'string'
                        ) {
                            element.val(value);
                        } else if (typeof element.data('child-id') !== 'undefined') {
                            //the value has to be set in the child element
                            const childId = element.data('child-id');
                            const childElement = $('#' + childId);

                            //to get the correct radio, we have to get the parent-id from the child element
                            //we can't use the name here, because on some pages there are radio-buttons with the same name to make
                            // only one of those options selectable (e.g. the audio_vbr/audio_bitrate selector)
                            const parentRadioId = childElement.data('parent-radio-id');
                            $('#' + parentRadioId).click();
                            const $optgroup = childElement.find('optgroup[data-options-target="' + this.target + '"]');

                            //deselect the default value
                            $optgroup.find('option[selected="selected"]').prop('selected', false);
                            const $option = $optgroup.find('option[value="' + value + '"]');
                            $option.attr('selected', 'selected');
                        }
                    });
                }
            }
        },
        deletePreset(preset) {
            this.setStatusLoading();
            presetApi
                .deletePreset(preset.id_preset)
                .then((response) => {
                    pageNotification.success('Preset deleted');
                })
                .catch((e) => {
                    globalLogger.log('preset selector error - delete preset', { error: e, presetId: preset.id_preset });

                    pageNotification.error('Something went wrong, please try again later!');
                })
                .finally(() => {
                    presetApi.getPersonalPresets().then((response) => {
                        response = JSON.parse(response.data);

                        this.presets = [];

                        response.forEach((preset) => {
                            this.presets.push(preset);
                        });

                        this.setStatusDone();
                    });
                });
        },
        initPatchPreset(preset) {
            if (this.patchPresetInit) {
                this.patchPresetInit = false;
                this.presetToUpdate = null;
                this.updatePresetName = '';
            } else {
                this.patchPresetInit = true;
                this.presetToUpdate = preset;
            }
        },
        patchPreset() {
            this.setStatusLoading();

            if (!testIsNonEmptyString(this.updatePresetName)) {
                this.updatePresetName = this.presetToUpdate.name;
            }

            const conversion = global.qgFeaturePage.getOptionsFromForm('#sendform');

            if (!conversion) {
                return;
            }

            const presetData = {
                category: conversion.category,
                target: conversion.target,
                name: this.updatePresetName,
                options: conversion.options,
            };

            presetApi
                .patchPreset(this.presetToUpdate.id_preset, presetData)
                .then((response) => {
                    pageNotification.success('Preset updated');
                })
                .catch((e) => {
                    globalLogger.log('preset selector error - patch preset', {
                        error: e,
                        presetId: this.presetToUpdate.id_preset,
                    });

                    pageNotification.error('Something went wrong, please try again later!');
                })
                .finally(() => {
                    presetApi.getPersonalPresets().then((response) => {
                        response = JSON.parse(response);

                        this.presets = [];

                        response.forEach((preset) => {
                            this.presets.push(preset);
                        });

                        this.patchPresetInit = false;
                        this.setStatusDone();
                    });
                });
        },
        changePresetName(changePresetName) {
            this.updatePresetName = changePresetName;
        },
    },
    data() {
        return {
            expanded: false,
            presets: [],
            presetName: '',
            updatePresetName: '',
            presetSaved: false,
            target: '',
            targetSelector: document.getElementById('inputTarget'),
            patchPresetInit: false,
            presetToUpdate: null,
        };
    },
};
</script>

<style lang="scss">
.vue-preset-dropdown {
    min-width: 600px;
    max-width: calc(100vw - 30px);

    @media (max-width: 630px) {
        min-width: auto;
        width: calc(100vw - 30px);
    }

    @media (max-width: 290px) {
        // important needed because bootstrap
        right: 0 !important;
    }
}
</style>
